/**
 * useMultiCartSync Hook
 *
 * Subscribes to user's carts WebSocket topic and invalidates
 * TanStack Query cache when cart is updated on any Storefront.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWebSocketOptional,
  useRealtimeEventOptional,
  EventType,
  RealtimeTopics,
  type CartAggregationUpdatedPayload,
} from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';
import { MULTI_CART_QUERY_KEY } from './useMultiCart';

/**
 * Hook to sync multi-cart data across apps via WebSocket
 *
 * Call this hook at app root level or in MultiCartProvider.
 * Automatically subscribes to user's carts topic and invalidates
 * queries when CART_UPDATED events are received.
 *
 * @example
 * ```tsx
 * function MultiCartProvider({ children }: { children: ReactNode }) {
 *   useMultiCartSync(); // Subscribe to real-time updates
 *   const { data } = useMultiCart();
 *   // ...
 * }
 * ```
 */
export function useMultiCartSync(): void {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const { isConnected, subscribe, unsubscribe } = useWebSocketOptional();

  // Subscribe to user's carts topic
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !user?.userGuid) {
      return;
    }

    const topic = RealtimeTopics.userCarts(user.userGuid);
    subscribe([topic]);

    return () => {
      unsubscribe([topic]);
    };
  }, [isConnected, isAuthenticated, user?.userGuid, subscribe, unsubscribe]);

  // Invalidate multi-cart query on CART_UPDATED event
  const invalidateMultiCart = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: MULTI_CART_QUERY_KEY });
  }, [queryClient]);

  // Handle CART_AGGREGATION_UPDATED events — published by Core Backend after DB write,
  // guaranteeing data is ready for refetch (no race with raw CART_UPDATED)
  useRealtimeEventOptional<CartAggregationUpdatedPayload>(
    EventType.CART_AGGREGATION_UPDATED,
    invalidateMultiCart,
    [invalidateMultiCart]
  );
}
